import { toast } from '@/components/ui/Toast/Toast';
import { captureError } from '@/config/sentry';
import { useGetStudentsSchedulingDetails, type GetStudentsSchedulingDetailsResponseData } from '@/features/scheduling/api/getStudentsSchedulingDetails';
import { getSelectedDeliveryCenterSchedulingDetails } from '@/features/scheduling/utils/getSelectedDeliveryCenterSchedulingDetails';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { isEnrollmentSchedulable } from '@/features/scheduling/utils/schedulability';
import type { ApiErrorResponse } from '@/lib/apiClient';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { Appointment, RecurrencePattern } from '@/types/scheduling';
import { subHours } from 'date-fns';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type RescheduleStep = 'selectNewDate' | 'summary' | 'success' | 'lateReschedule' | 'error';

export type ReschedulingErrorType = 'fullyBooked' | 'generic' | 'rescheduleFailed';

export interface RescheduleFlowData {
  config: {
    isLoading: boolean;
    error: ApiErrorResponse | null;
    data?: GetStudentsSchedulingDetailsResponseData;
  };
  appointment?: Appointment;
  newDateAndTime: string;
  applyToFollowingSessions: boolean;
  recurring: {
    recurrencePattern?: RecurrencePattern;
    endDate?: string;
  };
  rescheduledAppointmentDetails: {
    notpossible_appointments?: { date: string }[];
    booked_appointments?: Appointment[];
    notBookedAppointments?: { date: string }[];
  };
}

export interface RescheduleFlowContextType {
  currentStep: RescheduleStep;
  data: RescheduleFlowData;
  goToStep: (step: RescheduleStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  updateData: <K extends keyof RescheduleFlowData>(key: K, value: RescheduleFlowData[K]) => void;
  resetFlow: () => void;
  isRescheduleDialogOpen: boolean;
  setIsRescheduleDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeRescheduleDialog: () => void;
  openRescheduleDialog: (session: Appointment, openButtonRef?: React.RefObject<HTMLButtonElement | null>) => void;
  getOpenButtonRef: () => React.RefObject<HTMLButtonElement | null>; // Get the stored button ref
  errorType?: ReschedulingErrorType;
  setErrorType: (type: ReschedulingErrorType | undefined) => void;
}

const RescheduleFlowContext = createContext<RescheduleFlowContextType | undefined>(undefined);

export { RescheduleFlowContext };

const STEP_ORDER: RescheduleStep[] = ['selectNewDate', 'summary', 'success'];

const getInitialData = (
  isLoading: boolean,
  error: ApiErrorResponse | null,
  data?: GetStudentsSchedulingDetailsResponseData,
  appointment?: Appointment,
): RescheduleFlowData => ({
  appointment,
  newDateAndTime: '',
  applyToFollowingSessions: false,
  recurring: {},
  config: {
    isLoading,
    error,
    data,
  },
  rescheduledAppointmentDetails: {},
});

interface RescheduleFlowProviderProps {
  children: React.ReactNode;
}

export const RescheduleFlowProvider: React.FC<RescheduleFlowProviderProps> = ({ children }) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'rescheduleDialog' });

  const [currentStep, setCurrentStep] = useState<RescheduleStep>('selectNewDate');
  const [errorType, setErrorType] = useState<ReschedulingErrorType | undefined>();

  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);

  const schedulingDetailsQuery = useGetStudentsSchedulingDetails({});

  const [data, setData] = useState<RescheduleFlowData>(() =>
    getInitialData(schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data),
  );

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      config: {
        isLoading: schedulingDetailsQuery.isLoading,
        error: schedulingDetailsQuery.error,
        data: schedulingDetailsQuery.data,
      },
    }));
  }, [schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data]);

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const openButtonRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    const ref = timeoutRef;
    return () => {
      if (ref.current) {
        clearTimeout(ref.current);
      }
    };
  }, []);

  const goToStep = useCallback((step: RescheduleStep) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const updateData = useCallback(<K extends keyof RescheduleFlowData>(key: K, value: RescheduleFlowData[K]) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const resetFlow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCurrentStep('selectNewDate');
      setErrorType(undefined);
      setData((prev) => ({
        newDateAndTime: '',
        applyToFollowingSessions: false,
        recurring: {},
        appointment: undefined,
        config: prev.config,
        rescheduledAppointmentDetails: {},
      }));
    }, 300);
  }, []);

  const closeRescheduleDialog = useCallback(() => {
    void captureAnalyticsEvent('rescheduling_closed', { appointment_id: data.appointment?.appointment_id });
    setIsRescheduleDialogOpen(false);
    resetFlow();

    if (openButtonRef.current) {
      setTimeout(() => {
        openButtonRef.current?.focus();
      }, 0);
    }
  }, [resetFlow, data.appointment?.appointment_id]);

  const openRescheduleDialog = useCallback(
    (appointment: Appointment, buttonRef?: React.RefObject<HTMLButtonElement | null>) => {
      // Store the button ref that opened this dialog
      openButtonRef.current = buttonRef?.current ?? null;

      if (!data.config.data?.students) {
        throw new Error('Students scheduling details data is required to open the reschedule dialog');
      }

      // An upcoming appointment can reference an enrollment that scheduling-details no longer
      // returns (e.g. an expired/superseded enrollment). Rescheduling such an appointment cannot
      // be resolved online, so bail out gracefully instead of opening a form that would crash the
      // downstream steps — and report the endpoint mismatch to Sentry so it can be triaged.
      const appointmentEnrollment = getStudentEnrollmentDetails(data.config.data.students, appointment.student_id, appointment.enrollment_id);

      if (!appointmentEnrollment) {
        captureError(new Error('Reschedule blocked: appointment enrollment missing from scheduling details'), {
          appointment_id: appointment.appointment_id,
          enrollment_id: appointment.enrollment_id,
          student_id: appointment.student_id,
          delivery_type: appointment.delivery_type,
        });

        toast({
          variant: 'destructive',
          description: t('unavailableToast.description'),
        });

        return;
      }

      const studentSchedulingDetails = data.config.data.students.find((student) => student.id === appointment.student_id);
      if (!studentSchedulingDetails || !isEnrollmentSchedulable(appointmentEnrollment, studentSchedulingDetails)) {
        toast({
          variant: 'destructive',
          description: t('unavailableToast.description'),
        });

        return;
      }

      setData((prev) => ({
        ...getInitialData(prev.config.isLoading, prev.config.error, prev.config.data, appointment),
      }));

      const centerDetails = getSelectedDeliveryCenterSchedulingDetails(data.config.data.students, appointment.student_id, appointment.delivery_type);

      const minCancellationWindow = centerDetails?.minimum_cancellation_window_hours ?? 0;

      const freeCancellationUntil = appointment?.appointment_date && subHours(new Date(appointment.appointment_date), minCancellationWindow);

      const isLateReschedule = freeCancellationUntil ? new Date() > freeCancellationUntil : false;

      if (isLateReschedule) {
        setCurrentStep('lateReschedule');
      } else {
        setCurrentStep('selectNewDate');
      }

      setIsRescheduleDialogOpen(true);
    },
    [data.config.data?.students, t],
  );

  const getOpenButtonRef = useCallback(() => {
    return { current: openButtonRef.current };
  }, []);

  const contextValue = useMemo(
    () => ({
      currentStep,
      data,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      updateData,
      resetFlow,
      isRescheduleDialogOpen,
      setIsRescheduleDialogOpen,
      openRescheduleDialog,
      closeRescheduleDialog,
      getOpenButtonRef,
      errorType,
      setErrorType,
    }),
    [
      currentStep,
      data,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      updateData,
      resetFlow,
      isRescheduleDialogOpen,
      closeRescheduleDialog,
      openRescheduleDialog,
      setIsRescheduleDialogOpen,
      getOpenButtonRef,
      errorType,
      setErrorType,
    ],
  );

  return <RescheduleFlowContext value={contextValue}>{children}</RescheduleFlowContext>;
};
